/**
 * LogoSignaturePanel — S4 #logo
 * canonical: lines 2084–2099
 *
 * Drag-and-drop + click-to-upload for both logo and signature.
 * Images are auth-gated on the server, so we fetch as blob object URLs.
 * File constraints: PNG/JPG/GIF, auto-compressed server-side.
 */
import { useState, useEffect, useRef, useCallback, type MutableRefObject } from 'react'
import {
  uploadLogo, uploadSignature,
  fetchLogoObjectUrl, fetchSignatureObjectUrl,
  type CompanySettings,
} from '../../../api/settings'

type UploadStatus = 'idle' | 'uploading' | 'error'

interface UploadCellProps {
  label: string
  helpText: string
  objectUrl: string | null
  originalName: string | null
  status: UploadStatus
  errMsg: string
  dragging: boolean
  onFile: (f: File) => void
  onDragEnter: () => void
  onDragLeave: () => void
  testId: string
}

const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
const MAX_BYTES = 5 * 1024 * 1024 // 5 MB

function UploadCell({
  label, helpText, objectUrl, originalName, status, errMsg,
  dragging, onFile, onDragEnter, onDragLeave, testId,
}: UploadCellProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    onDragLeave()
    const file = e.dataTransfer.files[0]
    if (file) onFile(file)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onFile(file)
    e.target.value = ''
  }

  const hasFile = !!objectUrl

  return (
    <div
      className={`upload-cell${hasFile ? ' has-file' : ''}${dragging ? ' dragging' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); onDragEnter() }}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      data-testid={testId}
      role="button"
      tabIndex={0}
      aria-label={`Upload ${label}`}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click() } }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        style={{ display: 'none' }}
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
      <div className="upload-l">{label}</div>

      {hasFile ? (
        <>
          <img
            src={objectUrl!}
            alt={label}
            style={{ maxWidth: 80, maxHeight: 48, margin: '8px auto 0', display: 'block', borderRadius: 3 }}
          />
          {originalName && (
            <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 6 }}>{originalName}</div>
          )}
          <button
            className="btn b-gh btn-sm"
            style={{ marginTop: 8 }}
            onClick={e => { e.stopPropagation(); inputRef.current?.click() }}
            data-testid={`${testId}-replace-btn`}
          >
            Replace
          </button>
        </>
      ) : (
        <>
          <div className="upload-h">{helpText}</div>
          {status === 'error' ? (
            <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 6 }}>{errMsg}</div>
          ) : (
            <button
              className="btn b-ol btn-sm"
              style={{ marginTop: 10 }}
              disabled={status === 'uploading'}
              onClick={e => { e.stopPropagation(); inputRef.current?.click() }}
              data-testid={`${testId}-upload-btn`}
            >
              {status === 'uploading' ? 'Uploading…' : 'Click to upload'}
            </button>
          )}
        </>
      )}

      {status === 'uploading' && (
        <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 6 }}>Uploading…</div>
      )}
    </div>
  )
}

interface Props {
  settings: CompanySettings | null
  onSaved: (s: CompanySettings) => void
}

async function refreshPreviewUrls(
  settings: CompanySettings,
  setLogoUrl: (url: string | null) => void,
  setSigUrl: (url: string | null) => void,
  logoUrlRef: MutableRefObject<string | null>,
  sigUrlRef: MutableRefObject<string | null>,
) {
  if (settings.logo_filename) {
    const url = await fetchLogoObjectUrl()
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
    logoUrlRef.current = url
    setLogoUrl(url)
  } else {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
    logoUrlRef.current = null
    setLogoUrl(null)
  }
  if (settings.signature_filename) {
    const url = await fetchSignatureObjectUrl()
    if (sigUrlRef.current) URL.revokeObjectURL(sigUrlRef.current)
    sigUrlRef.current = url
    setSigUrl(url)
  } else {
    if (sigUrlRef.current) URL.revokeObjectURL(sigUrlRef.current)
    sigUrlRef.current = null
    setSigUrl(null)
  }
}

export default function LogoSignaturePanel({ settings, onSaved }: Props) {
  const [logoUrl,      setLogoUrl]      = useState<string | null>(null)
  const [sigUrl,       setSigUrl]       = useState<string | null>(null)
  const [logoStatus,   setLogoStatus]   = useState<UploadStatus>('idle')
  const [sigStatus,    setSigStatus]    = useState<UploadStatus>('idle')
  const [logoErr,      setLogoErr]      = useState('')
  const [sigErr,       setSigErr]       = useState('')
  const [logoDragging, setLogoDragging] = useState(false)
  const [sigDragging,  setSigDragging]  = useState(false)

  // Track object URLs for revocation on unmount
  const logoUrlRef = useRef<string | null>(null)
  const sigUrlRef  = useRef<string | null>(null)

  useEffect(() => {
    if (settings?.logo_filename) {
      fetchLogoObjectUrl().then(url => {
        if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
        logoUrlRef.current = url
        setLogoUrl(url)
      })
    }
    if (settings?.signature_filename) {
      fetchSignatureObjectUrl().then(url => {
        if (sigUrlRef.current) URL.revokeObjectURL(sigUrlRef.current)
        sigUrlRef.current = url
        setSigUrl(url)
      })
    }
    return () => {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
      if (sigUrlRef.current)  URL.revokeObjectURL(sigUrlRef.current)
    }
  }, [settings?.logo_filename, settings?.signature_filename])

  function validateFile(file: File): string | null {
    if (!ACCEPTED.includes(file.type)) return 'Only PNG, JPG, or GIF files accepted'
    if (file.size > MAX_BYTES) return `File too large (max 5 MB, got ${(file.size / 1024 / 1024).toFixed(1)} MB)`
    return null
  }

  const handleLogoFile = useCallback(async (file: File) => {
    const err = validateFile(file)
    if (err) { setLogoErr(err); setLogoStatus('error'); setTimeout(() => setLogoStatus('idle'), 4000); return }
    setLogoStatus('uploading')
    // Immediate local preview
    const localUrl = URL.createObjectURL(file)
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
    logoUrlRef.current = localUrl
    setLogoUrl(localUrl)
    try {
      const updated = await uploadLogo(file)
      onSaved(updated)
      await refreshPreviewUrls(updated, setLogoUrl, setSigUrl, logoUrlRef, sigUrlRef)
      setLogoStatus('idle')
    } catch (e) {
      setLogoErr((e as Error).message || 'Upload failed')
      setLogoStatus('error')
      setTimeout(() => setLogoStatus('idle'), 4000)
    }
  }, [onSaved])

  const handleSigFile = useCallback(async (file: File) => {
    const err = validateFile(file)
    if (err) { setSigErr(err); setSigStatus('error'); setTimeout(() => setSigStatus('idle'), 4000); return }
    setSigStatus('uploading')
    const localUrl = URL.createObjectURL(file)
    if (sigUrlRef.current) URL.revokeObjectURL(sigUrlRef.current)
    sigUrlRef.current = localUrl
    setSigUrl(localUrl)
    try {
      const updated = await uploadSignature(file)
      onSaved(updated)
      await refreshPreviewUrls(updated, setLogoUrl, setSigUrl, logoUrlRef, sigUrlRef)
      setSigStatus('idle')
    } catch (e) {
      setSigErr((e as Error).message || 'Upload failed')
      setSigStatus('error')
      setTimeout(() => setSigStatus('idle'), 4000)
    }
  }, [onSaved])

  return (
    <div id="logo" className="panel" data-testid="panel-logo" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Logo &amp; signature</div>
        <span className="panel-st">Appears on all pay app PDFs · auto-compressed · max 5 MB each</span>
      </div>
      <div className="upload-row">
        <UploadCell
          label="Company logo"
          helpText="PNG or JPG · shown on PDF header"
          objectUrl={logoUrl}
          originalName={settings?.logo_original_name ?? null}
          status={logoStatus}
          errMsg={logoErr}
          dragging={logoDragging}
          onFile={handleLogoFile}
          onDragEnter={() => setLogoDragging(true)}
          onDragLeave={() => setLogoDragging(false)}
          testId="logo-upload-cell"
        />
        <UploadCell
          label="Signature"
          helpText="PNG or JPG · shown at PDF bottom"
          objectUrl={sigUrl}
          originalName={settings?.signature_filename ? 'signature' : null}
          status={sigStatus}
          errMsg={sigErr}
          dragging={sigDragging}
          onFile={handleSigFile}
          onDragEnter={() => setSigDragging(true)}
          onDragLeave={() => setSigDragging(false)}
          testId="sig-upload-cell"
        />
      </div>
    </div>
  )
}
